import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { appPagePath } from '../../devmap';
import './usage.css';

const DAYS = 30;
const RECENT = 7;
// The busiest-endpoints table asks each active app for its own breakdown, so it
// stops at the apps doing most of the work rather than one request per app.
const ENDPOINT_APPS = 6;
const ENDPOINT_ROWS = 10;

const dayKey = (d) => d.toISOString().slice(0, 10);

/**
 * Every app's `calls_by_day_30d` summed into one series of 30 days, oldest
 * first and ending on `today` (UTC, the backend's day). The backend leaves out
 * days with no calls, so a missing day is a zero, not a gap.
 */
export function dailyCalls(apps, today = new Date()) {
  const end = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const days = Array.from({ length: DAYS }, (_, i) => dayKey(new Date(end - (DAYS - 1 - i) * 86400000)));
  const totals = new Map(days.map((d) => [d, 0]));
  (apps || []).forEach((app) => {
    (app.calls_by_day_30d || []).forEach(({ date, count }) => {
      if (totals.has(date)) totals.set(date, totals.get(date) + (count || 0));
    });
  });
  return days.map((date) => ({ date, count: totals.get(date) }));
}

/** The page's one count of an app's calls: its series, summed. */
const sumDays = (days) => days.reduce((s, d) => s + d.count, 0);

// One colour per app in "By app", in the table's order (main's chart palette).
export const SERIES_COLORS = [
  '#c04434', '#2b7fbf', '#3f8f5f', '#7a5cc2',
  '#c26a2e', '#4f63b8', '#c25f7e', '#a8871d',
];

/** Where an app's own page is: a plugin's is under /plugins, an app's Health panel under /applications. */
export const openPath = (app) => (app.is_plugin ? `/plugins/${app.app_id}` : appPagePath(app.app_id, 'health'));

const shortDate = (key) => new Date(`${key}T00:00:00Z`)
  .toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });

/**
 * The 30-day chart. `series` is one entry per app ({ id, name, color, days });
 * `stacked` draws each app as its own band of every day's bar, otherwise the
 * day's bar is the sum. Either way a bar is as tall as that day's total, so the
 * two views never disagree about a day.
 */
export function CallsChart({ series, stacked = false, label = 'All apps' }) {
  const [hover, setHover] = useState(null);
  const w = 600;
  const h = 120;
  const days = dailyTotals(series);
  const bw = w / days.length;
  const max = Math.max(1, ...days.map((d) => d.count));
  const total = sumDays(days);
  const on = hover == null ? null : days[hover];
  const scale = (n) => (n / max) * (h - 4);

  const tip = (i) => {
    const d = days[i];
    const head = `${shortDate(d.date)}: ${d.count.toLocaleString()} calls`;
    if (!stacked) return head;
    return [head, ...series.filter((s) => s.days[i].count > 0)
      .map((s) => `${s.name}: ${s.days[i].count.toLocaleString()}`)].join('\n');
  };

  return (
    <section className="usage-chart" aria-label="Calls per day, last 30 days">
      <div className="usage-chart__head">
        <span className="usage__title">{label}</span>
        <span className="usage-chart__read" data-testid="calls-readout">
          {on ? `${shortDate(on.date)} · ${on.count.toLocaleString()} calls` : `${total.toLocaleString()} calls`}
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" onMouseLeave={() => setHover(null)}>
        {days.map((d, i) => {
          const cls = i === hover ? 'is-on' : i < days.length - RECENT ? 'is-dim' : '';
          let y = h;
          return (
            <g key={d.date}>
              {stacked
                ? series.map((s) => {
                  const bh = scale(s.days[i].count);
                  if (!bh) return null;
                  y -= bh;
                  return (
                    <rect key={s.id} className={`usage-chart__band ${cls}`} data-app={s.id}
                      x={i * bw + 1} y={y} width={bw - 2} height={bh} style={{ fill: s.color }} />
                  );
                })
                : <rect className={`usage-chart__bar ${cls}`} x={i * bw + 1} y={h - scale(d.count)} width={bw - 2} height={scale(d.count)} />}
              <rect className="usage-chart__hit" x={i * bw} y={0} width={bw} height={h}
                onMouseEnter={() => setHover(i)}>
                <title>{tip(i)}</title>
              </rect>
            </g>
          );
        })}
        <line className="usage-chart__base" x1={0} x2={w} y1={h - 0.5} y2={h - 0.5} vectorEffect="non-scaling-stroke" />
      </svg>
      {total === 0 && <p className="usage-chart__none">No calls in the last 30 days.</p>}
      <div className="usage-chart__axis"><span>30 days ago</span><span>Today</span></div>
      {stacked && (
        <ul className="usage-chart__legend" aria-label="Apps">
          {series.map((s) => (
            <li key={s.id}><i style={{ background: s.color }} aria-hidden="true" />{s.name}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Each day's calls across `series`, summed. */
function dailyTotals(series) {
  const first = series[0]?.days || dailyCalls([]);
  return first.map((d, i) => ({ date: d.date, count: series.reduce((s, x) => s + x.days[i].count, 0) }));
}

function Stat({ label, value }) {
  return (
    <div className="usage__stat">
      <span className="usage__stat-label">{label}</span>
      <span className="usage__stat-n">{value.toLocaleString()}</span>
    </div>
  );
}

/**
 * Usage → Calls: every call your apps made in the last 30 days — one chart for
 * all of them (summed, or app by app), the apps that made them, and the
 * endpoints they hit hardest. Picking an app narrows the page to it, in place
 * (`?app=`), and adds who used it most; its own page is a separate "Open" link.
 *
 * ONE SOURCE FOR EVERY CALL COUNT. The overview gives each app a 30-day total
 * AND a per-day series, and they cover different windows: the total is a
 * rolling 30 × 24 hours, which reaches into a 31st calendar day the chart has
 * no bar for. Mixing them let the page say "8 calls" over an empty chart. So
 * every figure here — the strip, the chart, the table, the share — is summed
 * from the same 30 days of series, and the counts of apps and people from the
 * same apps list.
 */
export default function CallsPage({ groupId = null }) {
  const [params, setParams] = useSearchParams();
  const pickedId = params.get('app');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState(null); // { [app_id]: metrics } for `active`
  const [view, setView] = useState('all');      // 'all' summed | 'apps' one band per app

  useEffect(() => {
    let cancelled = false;
    const url = groupId ? `/third-party/my-apps/overview?group_id=${groupId}` : '/third-party/my-apps/overview';
    api.request(url)
      .then((r) => { if (!cancelled) setData(r || { apps: [] }); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load calls.'); });
    return () => { cancelled = true; };
  }, [groupId]);

  const apps = useMemo(() => {
    const today = new Date();
    return (data?.apps || [])
      .map((a) => {
        const days = dailyCalls([a], today);
        return { ...a, days, calls: sumDays(days), recent: sumDays(days.slice(-RECENT)) };
      })
      .sort((a, b) => b.calls - a.calls || String(a.app_name).localeCompare(String(b.app_name)))
      .map((a, i) => ({ ...a, color: SERIES_COLORS[i % SERIES_COLORS.length] }));
  }, [data]);
  const picked = useMemo(() => apps.find((a) => String(a.app_id) === pickedId) || null, [apps, pickedId]);
  const allTotal = useMemo(() => apps.reduce((s, a) => s + a.calls, 0), [apps]);
  const active = useMemo(() => (picked
    ? (picked.calls > 0 ? [picked] : [])
    : apps.filter((a) => a.calls > 0).slice(0, ENDPOINT_APPS)), [apps, picked]);

  // The per-app metrics call (the one an app's Health page uses) is the only
  // place endpoint counts and top users exist; it has no per-endpoint latency,
  // so no p95.
  useEffect(() => {
    if (!active.length) { setMetrics(null); return undefined; }
    let cancelled = false;
    setMetrics(null);
    Promise.all(active.map((app) => api.request(`/third-party/${app.app_id}/metrics`).catch(() => null)))
      .then((list) => {
        if (cancelled) return;
        setMetrics(Object.fromEntries(active.map((app, i) => [app.app_id, list[i]])));
      });
    return () => { cancelled = true; };
  }, [active]);

  if (error) return <p className="usage__msg is-bad">{error}</p>;
  if (!data) return <p className="usage__msg">Loading…</p>;

  const pick = (app) => {
    const next = new URLSearchParams(params);
    if (picked && String(picked.app_id) === String(app.app_id)) next.delete('app');
    else next.set('app', app.app_id);
    setParams(next, { replace: true });
  };
  const showAll = () => { const next = new URLSearchParams(params); next.delete('app'); setParams(next, { replace: true }); };

  // What the strip, chart and endpoints are about: the picked app, or all of them.
  const scope = picked ? [picked] : apps;
  const scopeDays = dailyCalls(scope);
  const nApps = apps.filter((a) => !a.is_plugin).length;
  const nPlugins = apps.length - nApps;
  const people = scope.reduce((s, a) => s + (a.authorized_users_count || 0), 0);
  // "By app" only says something with two apps to compare.
  const canCompare = !picked && apps.filter((a) => a.calls > 0).length > 1;
  const byApp = canCompare && view === 'apps';
  const series = (byApp ? apps.filter((a) => a.calls > 0) : scope)
    .map((a) => ({ id: a.app_id, name: a.app_name, color: a.color, days: a.days }));

  // Null until every app asked has answered (a failed one answers null).
  const loaded = metrics && active.every((app) => app.app_id in metrics) ? metrics : null;
  const endpoints = loaded && active
    .flatMap((app) => (loaded[app.app_id]?.calls_by_endpoint || []).map((e) => ({ ...e, app })))
    .sort((a, b) => b.count - a.count)
    .slice(0, ENDPOINT_ROWS);
  const topUsers = picked && picked.calls > 0
    ? (loaded ? [...(loaded[picked.app_id]?.top_users || [])].sort((a, b) => b.calls - a.calls) : null)
    : [];

  return (
    <div className="usage">
      {picked && (
        <p className="usage__picked">
          Showing <b>{picked.app_name}</b> only.
          <button type="button" className="usage__link" onClick={showAll}>Show all apps</button>
          <Link className="usage__link" to={openPath(picked)}>Open {picked.app_name} →</Link>
        </p>
      )}

      <div className="usage__stats" data-testid="usage-stats">
        <Stat label="Calls, 30 days" value={sumDays(scopeDays)} />
        <Stat label="Today" value={scopeDays[scopeDays.length - 1].count} />
        <Stat label="People who authorized" value={people} />
        {picked
          ? <Stat label="Last 7 days" value={picked.recent} />
          : <Stat label={nPlugins ? 'Apps · plugins' : 'Apps'} value={nPlugins ? `${nApps} · ${nPlugins}` : nApps} />}
      </div>

      <div className="usage-chart-wrap">
        {canCompare && (
          <div className="usage__toggle" role="group" aria-label="Chart">
            {[['all', 'All apps'], ['apps', 'By app']].map(([id, text]) => (
              <button key={id} type="button" aria-pressed={view === id} className={view === id ? 'is-on' : ''}
                onClick={() => setView(id)}>{text}</button>
            ))}
          </div>
        )}
        <CallsChart series={series} stacked={byApp} label={picked ? picked.app_name : byApp ? 'By app' : 'All apps'} />
      </div>

      <section>
        <div className="usage__head">
          <h2 className="usage__title">By app</h2>
          <span className="usage__aside">
            {nApps} {nApps === 1 ? 'app' : 'apps'}{nPlugins ? `, ${nPlugins} ${nPlugins === 1 ? 'plugin' : 'plugins'}` : ''}
          </span>
        </div>
        <div className="utable-wrap">
          <table className="utable">
            <thead>
              <tr>
                <th className="is-left">Name</th>
                <th>People</th>
                <th>Calls 30d</th>
                <th>Last 7 days</th>
                <th>Share</th>
                <th aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {apps.map((app) => {
                const share = allTotal ? Math.round((app.calls / allTotal) * 100) : 0;
                const on = picked && String(picked.app_id) === String(app.app_id);
                return (
                  <tr key={app.app_id} className={`is-link${on ? ' is-on' : ''}`} tabIndex={0}
                    aria-selected={on ? 'true' : 'false'} onClick={() => pick(app)}
                    onKeyDown={(e) => e.key === 'Enter' && pick(app)}>
                    <td className="is-left">
                      <span className="utable__name">
                        {byApp && app.calls > 0 && <i className="utable__swatch" style={{ background: app.color }} aria-hidden="true" />}
                        {app.app_name}
                      </span>
                      <span className="utable__sub">{app.is_plugin ? 'Plugin' : 'App'}</span>
                    </td>
                    <td>{(app.authorized_users_count ?? 0).toLocaleString()}</td>
                    <td>{app.calls.toLocaleString()}</td>
                    <td>{app.recent.toLocaleString()}</td>
                    <td>
                      <span className="utable__share">
                        <span className="utable__muted">{share}%</span>
                        <span className="utable__share-rule" aria-hidden="true"><i style={{ width: `${share}%` }} /></span>
                      </span>
                    </td>
                    <td>
                      <Link className="usage__link" to={openPath(app)}
                        onClick={(e) => e.stopPropagation()}>Open →</Link>
                    </td>
                  </tr>
                );
              })}
              {apps.length === 0 && (
                <tr><td colSpan={6} className="utable__empty">No apps yet. Calls show up here once one is registered.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {active.length > 0 && (
        <section>
          <div className="usage__head"><h2 className="usage__title">{picked ? `Busiest endpoints · ${picked.app_name}` : 'Busiest endpoints'}</h2></div>
          <div className="utable-wrap">
            <table className="utable is-dense">
              <thead>
                <tr><th className="is-left">Endpoint</th><th className="is-left">App</th><th>Calls</th></tr>
              </thead>
              <tbody>
                {endpoints === null && <tr><td colSpan={3} className="utable__empty">Loading…</td></tr>}
                {endpoints?.length === 0 && <tr><td colSpan={3} className="utable__empty">No endpoint breakdown yet.</td></tr>}
                {endpoints?.map((e) => (
                  <tr key={`${e.app.app_id} ${e.method} ${e.endpoint}`}>
                    <td className="is-left utable__mono"><span className="utable__method">{e.method}</span>{e.endpoint}</td>
                    <td className="is-left">{e.app.app_name}</td>
                    <td>{e.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {picked && (
        <section>
          <div className="usage__head"><h2 className="usage__title">{`Top users · ${picked.app_name}`}</h2></div>
          <div className="utable-wrap">
            <table className="utable is-dense">
              <thead>
                <tr><th className="is-left">Person</th><th>Calls</th><th>Share</th></tr>
              </thead>
              <tbody>
                {topUsers === null && <tr><td colSpan={3} className="utable__empty">Loading…</td></tr>}
                {topUsers?.length === 0 && <tr><td colSpan={3} className="utable__empty">Nobody has used it in the last 30 days.</td></tr>}
                {topUsers?.map((u) => {
                  const share = picked.calls ? Math.min(100, Math.round((u.calls / picked.calls) * 100)) : 0;
                  return (
                    <tr key={u.user_id}>
                      <td className="is-left">{u.username || String(u.user_id).slice(0, 8)}</td>
                      <td>{(u.calls ?? 0).toLocaleString()}</td>
                      <td>
                        <span className="utable__share">
                          <span className="utable__muted">{share}%</span>
                          <span className="utable__share-rule" aria-hidden="true"><i style={{ width: `${share}%` }} /></span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
